import { apiClient } from './client';
import { Reservation } from '../types';

export const reservationApi = {
  // Generate temporary reservation number (for orderId before payment)
  generateTempReservationNumber: async (): Promise<string> => {
    const response = await apiClient.get<{ reservation_number: string }>('/reservations/generate-temp-number');
    return response.data.reservation_number;
  },

  // Create a new reservation
  createReservation: async (
    reservationData: Omit<Reservation, 'id' | 'status' | 'created_at' | 'updated_at'>
  ): Promise<Reservation> => {
    const response = await apiClient.post<Reservation>('/reservations', reservationData);
    return response.data;
  },

  // Get reservation by ID
  getReservationById: async (id: number): Promise<Reservation> => {
    const response = await apiClient.get<Reservation>(`/reservations/${id}`);
    return response.data;
  },

  // Update reservation
  updateReservation: async (
    id: number,
    data: Partial<Reservation>
  ): Promise<Reservation> => {
    const response = await apiClient.patch<Reservation>(`/reservations/${id}`, data);
    return response.data;
  },

  // Check reservation by customer info (public - no auth required)
  checkReservationPublic: async (
    customerName: string,
    customerPhone: string
  ): Promise<any> => {
    const response = await apiClient.post('/reservations/check-public', null, {
      params: {
        customer_name: customerName,
        customer_phone: customerPhone,
      },
    });
    return response.data;
  },

  // Cancel reservation (public - no auth required)
  cancelReservationPublic: async (endIdx: string): Promise<any> => {
    const response = await apiClient.post(`/reservations/cancel-public/${endIdx}`);
    return response.data;
  },

  // Get user's reservations (requires JWT auth)
  getUserReservations: async (params?: {
    days?: number;
    start_date?: string;
    end_date?: string;
  }): Promise<Reservation[]> => {
    const response = await apiClient.get<Reservation[]>('/reservations/my-reservations', {
      params,
    });
    return response.data;
  },

  // Check if extension is available
  checkExtensionAvailability: async (
    reservationId: number,
    newEndDate: string
  ): Promise<{
    available: boolean;
    message: string;
    current_end_date: string;
    new_end_date: string;
    additional_amount?: number;
    rental_fee?: number;
    insurance_fee?: number;
    extension_hours?: number;
    extension_days?: number;
    weekday_rate?: number;
    weekend_rate?: number;
  }> => {
    const response = await apiClient.post(
      `/reservations/${reservationId}/check-extension-availability`,
      { new_end_date: newEndDate }
    );
    return response.data;
  },

  // Extend reservation
  extendReservation: async (
    reservationId: number,
    newEndDate: string,
    paymentKey: string,
    orderId: string,
    amount: number
  ): Promise<{
    message: string;
    reservation_id: number;
    old_end_date: string;
    new_end_date: string;
  }> => {
    const response = await apiClient.post(
      `/reservations/${reservationId}/extend`,
      {
        new_end_date: newEndDate,
        payment_key: paymentKey,
        order_id: orderId,
        amount: amount
      }
    );
    return response.data;
  },

  // Calculate price
  calculatePrice: async (
    startDate: string,
    endDate: string,
    vehicleIdx: number
  ): Promise<{
    total_amount: number;
    total_hours: number;
    weekday_rate: number;
    weekend_rate: number;
  }> => {
    const response = await apiClient.post('/reservations/calculate-price', {
      start_date: startDate,
      end_date: endDate,
      vehicle_idx: vehicleIdx,
    });
    return response.data;
  },
};
